import * as _ from 'lodash'
import Decimal from 'decimal.js'
import { isNil } from 'lodash'
import BaseModel from 'base/model/base_model'
import articleConfig from 'article/config'
import orgamax from 'ampersand-app'
import { getAppState } from '@/redux/store'
import lang from '@/lang'

export const EMPTY_CATEGORY = 'Keine Angabe'

export default BaseModel.extend({
  urlRoot: articleConfig.resourceUrl,

  constructor: function ArticleModel() {
    BaseModel.apply(this, arguments)
    if (orgamax.user && isNil(this.vatPercent)) {
      const companyData = getAppState()?.settings?.company
      const isVatExempt = companyData?.defaultRevenueAccount !== null && companyData?.defaultRevenueAccount !== ''
      this.vatPercent = isVatExempt ? 0 : orgamax.user.getDefaultVat().value
    }
  },

  props: {
    id: ['string'],
    mandator: ['number'],
    number: ['string', true],
    title: ['string', true],
    description: ['string'],
    matchCode: ['string'],
    ean: ['string'],
    notes: ['string'],
    notesAlert: ['boolean'],
    category: ['string'],
    unit: ['string'],
    images: ['array', true, () => []],
    salesVolumeData: ['object'],
    weight: ['number', false, 0.00],
    minimumStockAmount: ['number', false],
    kind: ['string', true, 'salesAndPurchase'],
    isVoucher: ['boolean', true, false],

    // prices
    calculationBase: { type: 'string', required: true, values: ['net', 'gross'], default: 'net' },
    purchasePrice: { type: 'number', default: 0.00 },
    purchasePriceGross: { type: 'number', default: 0.00 },
    price: ['number', true, 0.00],
    priceGross: ['number', true, 0.00],
    laborShare: ['number', false, 0.00],
    laborShareGross: ['number', false, 0.00],
    vatPercent: { type: 'number' },

    // stockmanagment
    stock: ['object', true, () => ({ stockAmount: 0, reservedAmount: 0, availableAmount: 0 })],
    isStockManaged: ['boolean', true, false],
    bookkeepingAccountId: ['string', false, null],

    // pos
    isPosArticle: ['boolean', true, false],

    // differentialTax
    isDifferentialTax: ['boolean', true, false],
    differentialTaxTexts: ['object', true, () => ({ label: lang.differentialTax.differentialTaxLabel, name: lang.differentialTax.differentialTaxName })],

    // tag
    tags: ['array', true, () => []],

    // ecommerce shops
    ecommerceReferences: ['array', false, () => []],

    // graduated Price List
    isGraduatedPriceManaged: ['boolean', true, false],
    graduatedPriceList: ['array', true, () => ([])],

    // supplier article reference
    supplierList: ['array', true, () => ([])],

    customValues: ['object', true, () => ({})],
  },

  derived: {
    tempId: {
      deps: ['id'],
      fn: function() {
        return this.id || _.uniqueId('temp')
      },
    },
    displayVatPercent: {
      deps: ['vatPercent'],
      fn: function() {
        return `${this.vatPercent}%`.replace('.', ',')
      },
    },
    displayCategory: {
      deps: ['category'],
      fn: function() {
        return this.category || EMPTY_CATEGORY
      },
    },
  },

  /**
   * Calculates the sales prices based on the actual values and sets them to the specific model properties
   */
  calculate: function() {
    if (this.calculationBase === 'net') {
      this.setPricesBasedOnNet(this.price)
    } else {
      this.setPricesBasedOnGross(this.priceGross)
    }
  },

  /**
   * Sets price net and calculates price gross based on net and tax
   *
   * @param {Number} priceNet price net for the given article
   */
  setPricesBasedOnNet: function(priceNet) {
    const calculatedVat = 1 + (this.vatPercent / 100)
    const net = new Decimal(priceNet)
    const gross = net.times(calculatedVat)

    this.set({
      calculationBase: 'net',
      price: net.toDP(2).toNumber(),
      priceGross: gross.toDP(2).toNumber(),
    })
  },

  /**
   * Sets price gross and calculates price net based on gross and tax
   *
   * @param {Number} priceGross price gross for the given article
   */
  setPricesBasedOnGross: function(priceGross) {
    const calculatedVat = 1 + (this.vatPercent / 100)
    const gross = new Decimal(priceGross)
    const net = gross.dividedBy(calculatedVat)

    this.set({
      calculationBase: 'gross',
      priceGross: gross.toDP(2).toNumber(),
      price: net.toDP(2).toNumber(),
    })
  },

  /**
   * Sets vat code and recalculates sales prices
   *
   * @param {string} vatPercentString
   */
  setVatPercent: function(vatPercentString) {
    if (!vatPercentString) return

    const vatPercent = new Decimal(vatPercentString).toDP(2).toNumber()
    this.set({ vatPercent })
    this.calculate()
  },

  /**
   * Sets purchase price net and calculates purchase price gross based on net and tax
   *
   * @param {Number} purchasePriceNet purchase price net for the given article
   */
  setPurchasePricesBasedOnNet: function(purchasePriceNet) {
    const calculatedVat = 1 + (this.vatPercent / 100)
    const net = new Decimal(purchasePriceNet)
    const gross = net.times(calculatedVat)

    this.set({
      purchasePrice: net.toDP(2).toNumber(),
      purchasePriceGross: gross.toDP(2).toNumber(),
    })
  },

  /**
   * Sets purchase price gross and calculates purchase price net based on gross and tax
   *
   * @param {Number} purchasePriceGross purchase price gross for the given article
   */
  setPurchasePricesBasedOnGross: function(purchasePriceGross) {
    const calculatedVat = 1 + (this.vatPercent / 100)
    const gross = new Decimal(purchasePriceGross)
    const net = gross.dividedBy(calculatedVat)

    this.set({
      purchasePriceGross: gross.toDP(2).toNumber(),
      purchasePrice: net.toDP(2).toNumber(),
    })
  },

  /**
   * Sets vat code and recalculates purchase prices
   *
   * @param {string} vatPercentString
   */
  setVatPercentForPurchase: function(vatPercentString) {
    if (!vatPercentString) return

    const vatPercent = new Decimal(vatPercentString).toDP(2).toNumber()
    this.set({ vatPercent })

    // Recalculate purchase prices based on net price if available
    if (this.purchasePrice) {
      this.setPurchasePricesBasedOnNet(this.purchasePrice)
    }
  },

  getVatDate: function() {
    return this.date
  },
})
